import {
  type Level,
  type WordLevel,
  formatPronunciationLevelLabel,
  formatWordLevelLabel,
} from "@-/dev.improve.haniverse.lib.level";

export type IndexedLevel = { index: number; isLocked?: boolean; level: Level };
/**
 * The pronunciation level following a character level (`2-P1` after Level 2): the characters of the
 * preceding meaning-only levels, whose readings it introduces. It shares that level's premium lock.
 */
export type IndexedPronunciationLevel = {
  isLocked?: boolean;
  /** Zero-based position among pronunciation levels in the list (feeds the `-P#` label). */
  pronunciationIndex?: number;
  pronunciationLevel: Level;
};
/**
 * A word level in the merged curriculum, listed directly after the character level it follows
 * (`11-W14`, …). It shares that character level's premium lock.
 */
export type IndexedWordLevel = { isLocked?: boolean; wordLevel: WordLevel };
export type LevelsListEntry = IndexedLevel | IndexedPronunciationLevel | IndexedWordLevel;

export function isIndexedWordLevel(entry: LevelsListEntry): entry is IndexedWordLevel {
  return "wordLevel" in entry;
}

export function isIndexedPronunciationLevel(
  entry: LevelsListEntry,
): entry is IndexedPronunciationLevel {
  return "pronunciationLevel" in entry;
}

/** A character level: neither a pronunciation level nor a word level. */
export function isIndexedCharacterLevel(entry: LevelsListEntry): entry is IndexedLevel {
  return "level" in entry;
}

/**
 * "Level 11" / "Level 2-P1" / "Level 11-W14", with the level file's authored name appended as
 * ". {name}" when present (a character level's `name`, a pronunciation level's
 * `pronunciationName`).
 */
export function getLevelsListEntryLabel(entry: LevelsListEntry): string {
  if (isIndexedWordLevel(entry)) {
    return `Level ${formatWordLevelLabel(entry.wordLevel)}`;
  }
  if (isIndexedPronunciationLevel(entry)) {
    const numeric = `Level ${formatPronunciationLevelLabel(
      entry.pronunciationLevel,
      entry.pronunciationIndex ?? 0,
    )}`;
    const name = entry.pronunciationLevel.pronunciationName;
    if (name !== undefined && name !== "") {
      return `${numeric}. ${name}`;
    }
    return numeric;
  }
  const numeric = `Level ${entry.index + 1}`;
  const name = entry.level.name;
  if (name !== undefined && name !== "") {
    return `${numeric}. ${name}`;
  }
  return numeric;
}
